import { useCallback } from 'react';

import { useCustomizationExternalActions as useExternalActions } from '@sg/shared-fe-features-customization';

import { useTelemetry } from '@order/Telemetry';

// ───────────────────────────────────────────────────────────────────────────────

export const useCustomizationExternalActions = (params: Params) => {
  const { handleCustomizationComplete } = params;

  const { track } = useTelemetry();

  // ─── Actions ─────────────────────────────────────────────────────────

  const onOutOfStockIngredientsFound = useCallback<Callbacks['onOutOfStockIngredientsFound']>(
    (args) => {
      track('86ingredient_error', {
        restaurant: args.restaurant,
        product: args.product,
        ingredient: args.ingredient,
      });
    },
    [track],
  );

  const onMaxModificationsExceeded = useCallback<Callbacks['onMaxModificationsExceeded']>(() => {
    track('modify.max_modifications');
  }, [track]);

  const onMaxKindExceeded = useCallback<Callbacks['onMaxKindExceeded']>(
    (args) => {
      track('modify.max_kind_limit_exceeded', { kind: args.groupName });
    },
    [track],
  );

  const onModificationAdded = useCallback<Callbacks['onModificationAdded']>(
    (args) => {
      track('modify.ingredient_added', { ingredient: args.ingredient });
    },
    [track],
  );

  const onModificationRemoved = useCallback<Callbacks['onModificationRemoved']>(
    (args) => {
      track('modify.ingredient_removed', { ingredient: args.ingredient });
    },
    [track],
  );

  const onIngredientsSwapped = useCallback<Callbacks['onIngredientsSwapped']>(
    (args) => {
      track('modify.ingredients_swapped', {
        ingredients: args.promotions
          .map(
            (promotion) => `${promotion.ingredientName} (${promotion.includedQuantity}x portions)`,
          )
          .join(', '),
        count: args.promotions.length,
      });
    },
    [track],
  );

  const onCustomizationEnd = useCallback<Callbacks['onCustomizationEnd']>(() => {
    track('modify.done');
  }, [track]);

  const onCustomizationCancel = useCallback<Callbacks['onCustomizationCancel']>(() => {
    track('modify.cancel');
  }, [track]);

  const onCustomizationComplete = handleCustomizationComplete;

  // ─────────────────────────────────────────────────────────────────────

  return useExternalActions({
    onOutOfStockIngredientsFound,
    onMaxModificationsExceeded,
    onMaxKindExceeded,
    onModificationAdded,
    onModificationRemoved,
    onIngredientsSwapped,
    onCustomizationEnd,
    onCustomizationCancel,
    onCustomizationComplete,
  });
};

// ───── Types ───────────────────────────────────────────────────────────────────

type Params = {
  handleCustomizationComplete?: Callbacks['onCustomizationComplete'];
};

type Callbacks = Required<Parameters<typeof useExternalActions>[0]>;
